export interface AudioLimit {
  dailyLimit: number;
  usedToday: number;
  remaining: number;
  resetsAt: string | null;
  isLimitReached: boolean;
}

export class AudioLimitError extends Error {
  constructor(public readonly audioLimit: AudioLimit) {
    super('Daily audio transcription limit reached');
    this.name = 'AudioLimitError';
  }
}

export interface SpeechToTextResponse {
  text?: string;
  error?: string;
  audioLimit?: AudioLimit;
}

/**
 * Fire: Upload audio blob to backend, returns pollingKey immediately.
 */
const fireSpeechToText = async (audioBlob: Blob): Promise<string> => {
  const formData = new FormData();
  const baseMimeType = audioBlob.type.split(';')[0];
  const ext = baseMimeType.includes('mp4') ? 'mp4' : baseMimeType.includes('ogg') ? 'ogg' : 'webm';
  formData.append('audio', audioBlob, `audio.${ext}`);
  return (await window.PMW.writeLocal('aitranscript/speechToText', formData, 'json', {
    processData: false,
    contentType: false,
  })) as Promise<string>;
};

/**
 * Poll: Check transcription status using pollingKey.
 */
const checkSpeechToTextStatus = async (pollingKey: string): Promise<SpeechToTextResponse> => {
  return (await window.PMW.readLocal('aitranscript/getSpeechToTextStatus', {
    pollingKey,
  })) as SpeechToTextResponse;
};

/**
 * Combined fire + poll: Upload audio and wait for transcribed text.
 * Returns both the transcribed text and updated audio limit state.
 * Throws AudioLimitError if the daily limit is reached.
 */
export const transcribeAudioToText = async (audioBlob: Blob): Promise<{text: string; audioLimit: AudioLimit | null}> => {
  const res = (await window.PMW.pollingAjaxCallAsync(fireSpeechToText.bind(null, audioBlob), checkSpeechToTextStatus.bind(null))) as SpeechToTextResponse;

  if (res.error === 'limit_reached' && res.audioLimit) {
    throw new AudioLimitError(res.audioLimit);
  }

  if (res.error) {
    throw new Error(res.error);
  }

  if (res.text) {
    return {text: res.text, audioLimit: res.audioLimit ?? null};
  }

  throw new Error('No text received from speech-to-text');
};

/**
 * Fetch current audio limit status for the authenticated user.
 */
export const fetchAudioLimitStatus = async (): Promise<AudioLimit> => {
  return (await window.PMW.readLocal('aitranscript/getAudioLimitStatus')) as AudioLimit;
};
